import React, { useState, useRef } from "react";
import { Loader2, MapPin, Upload, AlertTriangle, Shield, ChevronRight, X, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "olovligt", label: "Bygge utan lov", desc: "Misstänkt olovligt byggande" },
  { key: "avvikelse", label: "Avvikelse från bygglov", desc: "Byggs annorlunda än beviljat" },
  { key: "farligt", label: "Farlig byggnad", desc: "Bristande säkerhet eller skick" },
  { key: "anvandning", label: "Otillåten användning", desc: "Lokal används på fel sätt" },
  { key: "annat", label: "Annat", desc: "Övriga tillsynsfrågor" },
];

export default function ReportSupervision() {
  const [form, setForm] = useState({
    address: "", fastighet: "", category: "",
    description: "", anonymous: true,
    reporter_name: "", reporter_email: "", reporter_phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.warning("Välj kategori"); return; }
    if (form.address.length < 3 || form.description.length < 10) {
      toast.warning("Adress och beskrivning behövs"); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/public/tillsyn-report", form);
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skicka in anmälan");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-fade-in-up">
        <div className="w-16 h-16 mx-auto bg-emerald-100 text-emerald-700 flex items-center justify-center rounded-full">
          <CheckCircle2 className="w-8 h-8 stroke-[1.5]" />
        </div>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
          Tack för din anmälan
        </h1>
        <p className="mt-3 text-zinc-600">
          Din anmälan är registrerad med referens <span className="font-semibold text-zinc-900">{submitted.ref}</span> och granskas av handläggare snarast.
          {form.anonymous ? " Du valde att vara anonym – vi har inte sparat dina kontaktuppgifter." : " Du blir kontaktad om vi behöver mer information."}
        </p>
        <div className="mt-8 inline-block bg-zinc-50 border border-zinc-200 rounded-sm p-5 text-left text-sm text-zinc-700">
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Vad händer nu?</div>
          <ol className="space-y-1.5 list-decimal list-inside">
            <li>Handläggaren bedömer om anmälan faller under PBL.</li>
            <li>Om relevant öppnas ett tillsynsärende.</li>
            <li>Berörd fastighetsägare informeras och får yttra sig.</li>
            <li>Beslut fattas: avskrivning, föreläggande eller annan åtgärd.</li>
          </ol>
        </div>
        <button onClick={() => { setSubmitted(null); setForm({ address: "", fastighet: "", category: "", description: "", anonymous: true, reporter_name: "", reporter_email: "", reporter_phone: "" }); }}
          className="mt-8 text-sm text-blue-700 hover:underline">
          ← Anmäl en annan överträdelse
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <Shield className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Tillsyn</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Anmäl misstänkt överträdelse</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600">
        Misstänker du att någon bygger utan lov eller avviker från beviljat bygglov? Anmäl här – kommunens byggnadsnämnd granskar varje anmälan.
      </p>

      <form onSubmit={submit} className="mt-8 bg-white border border-zinc-200 rounded-sm p-6 lg:p-8 space-y-5">
        {/* Category */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Vad gäller anmälan?</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key} type="button"
                data-testid={`category-${c.key}`}
                onClick={() => setForm({ ...form, category: c.key })}
                className={`text-left p-4 border rounded-sm transition ${
                  form.category === c.key
                    ? "border-blue-700 bg-blue-50 ring-2 ring-blue-100"
                    : "border-zinc-200 bg-white hover:border-blue-400"
                }`}
              >
                <div className="font-medium text-sm">{c.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{c.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Adress
          </label>
          <input
            required data-testid="report-address"
            value={form.address} onChange={set("address")}
            placeholder="t.ex. Storgatan 12, 11455 Stockholm"
            className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base"
          />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Fastighetsbeteckning (valfri)</label>
          <input data-testid="report-fastighet"
            value={form.fastighet} onChange={set("fastighet")}
            placeholder="t.ex. Vasastaden 1:43"
            className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-base" />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Beskriv vad du iakttagit</label>
          <textarea required rows={5} data-testid="report-description"
            value={form.description} onChange={set("description")}
            placeholder="Beskriv så detaljerat du kan: vad som byggts, sedan när, höjd/storlek, närhet till tomtgräns m.m."
            className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-base resize-none" />
          <div className="mt-1 text-xs text-zinc-500">Min 10 tecken · {form.description.length} tecken</div>
        </div>

        {/* Anonymous toggle */}
        <div className="border border-zinc-200 rounded-sm p-4 bg-zinc-50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="report-anonymous"
              checked={form.anonymous} onChange={set("anonymous")} className="mt-0.5" />
            <div>
              <div className="text-sm font-medium text-zinc-900">Anmäl anonymt</div>
              <div className="text-xs text-zinc-600">Vi sparar inte ditt namn eller kontaktuppgifter. Du blir då inte kontaktad om uppföljning.</div>
            </div>
          </label>
        </div>

        {!form.anonymous && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Namn</label>
              <input data-testid="report-name" value={form.reporter_name} onChange={set("reporter_name")}
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">E-post</label>
              <input type="email" data-testid="report-email" value={form.reporter_email} onChange={set("reporter_email")}
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Telefon</label>
              <input data-testid="report-phone" value={form.reporter_phone} onChange={set("reporter_phone")}
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900 flex gap-3">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Viktigt:</strong> Anmälningar prövas alltid mot PBL. Civilrättsliga tvister, buller eller störningar handläggs inte av byggnadsnämnden.
          </div>
        </div>

        <button type="submit" data-testid="report-submit" disabled={loading}
          className="w-full bg-blue-700 hover:bg-blue-800 text-white py-3.5 rounded-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98]">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Skickar...</> : <>Skicka in anmälan <ChevronRight className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}
